import { FunctionComponent, useContext, useEffect, useState } from 'react'
import { Box, Button, Chip, CircularProgress, IconButton, Stack, Typography } from '@mui/material'
import { Close, DownloadOutlined, InsertDriveFileOutlined, UploadFileOutlined, VisibilityOutlined } from '@mui/icons-material'
import moment from 'moment'
import EmissionService from '../../../../shared/services/emission/emission.service'
import UploadedResourceService from '../../../../shared/services/uploaded-resource/uploaded-resource.service'
import { IEmissionDocument } from '../../../../shared/services/emission/emission.model'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { LoadingSpinner } from '../../display-elements-components/loading-spinner'
import { DocumentPreviewDialog, isPreviewableDocument } from '../../display-elements-components/document-preview-dialog'

const ACCEPTED_FILE_TYPES = 'application/pdf,image/png,image/jpeg,image/gif,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

function formatSize(size: number | null): string {
  if (!size) return ''
  return `${Math.max(1, Math.round(size / 1024))} KB`
}

/** The three calls a record's document list needs. The emission default is built from EmissionService. */
export interface IEvidenceDocumentsApi {
  list: () => Promise<IEmissionDocument[]>;
  upload: (files: File[]) => Promise<IEmissionDocument[]>;
  remove: (resourceId: string) => Promise<IEmissionDocument[]>;
}

export interface IEvidencePanelCopy {
  intro: string;
  empty: string;
  removeMessage: (filename: string) => string;
  uploadLabel: string;
  uploadedMessage: string;
  removedMessage: string;
}

const EMISSION_COPY: IEvidencePanelCopy = {
  intro: 'Supporting documents for this emission, such as invoices, bills and meter photos. They appear alongside the emission in the audit pack.',
  empty: 'No evidence documents are attached to this emission.',
  removeMessage: (filename) => `Are you sure you want to remove "${filename}" from this emission? The stored file is kept for audit history and any other emissions it supports.`,
  uploadLabel: 'Upload evidence',
  uploadedMessage: 'Evidence uploaded',
  removedMessage: 'Evidence removed',
}

/**
 * Evidence documents for one record. By default the record is an emission (manual uploads plus
 * any bill-extracted source documents); any other record passes its own `documentsApi` and a
 * `resourceKey` the load effect keys on. Removing a document only detaches it from the record;
 * the stored file is always retained (an extracted bill can back several emissions).
 */
export const EmissionEvidencePanel: FunctionComponent<{
  emissionId?: string,
  canEdit: boolean,
  documentsApi?: IEvidenceDocumentsApi,
  resourceKey?: string,
  copy?: Partial<IEvidencePanelCopy>,
  acceptedFileTypes?: string,
  // Show an eye on PDF and image rows that opens the document inline.
  previewable?: boolean,
}> = ({ emissionId, canEdit, documentsApi, resourceKey, copy, acceptedFileTypes, previewable = false }) => {
  const emissionService = new EmissionService()
  const uploadedResourceService = new UploadedResourceService()
  const userNotificationsContext = useContext(UserNotificationsContext)
  const [documents, setDocuments] = useState<IEmissionDocument[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isUploading, setIsUploading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [previewDocument, setPreviewDocument] = useState<IEmissionDocument | null>(null)

  const text: IEvidencePanelCopy = { ...EMISSION_COPY, ...(copy || {}) }
  const accept = acceptedFileTypes || ACCEPTED_FILE_TYPES
  const api: IEvidenceDocumentsApi = documentsApi || {
    list: () => emissionService.getDocuments(emissionId || ''),
    upload: (files) => emissionService.uploadDocuments(emissionId || '', files),
    remove: (resourceId) => emissionService.removeDocument(emissionId || '', resourceId),
  }
  // A string key, not the api object: a caller passing a fresh object literal each render must
  // not re-fetch in a loop.
  const loadKey = resourceKey ?? emissionId ?? ''

  useEffect(() => {
    let isActive = true
    setIsLoading(true)
    setErrorMessage('')
    api.list()
      .then((response) => {
        if (isActive) setDocuments(response)
      })
      .catch((err) => {
        console.error(err)
        if (isActive) setErrorMessage('Unable to load the evidence documents. Please try again.')
      })
      .finally(() => {
        if (isActive) setIsLoading(false)
      })
    return () => {
      isActive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadKey])

  const uploadHandler = async (fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return
    setIsUploading(true)
    try {
      const response = await api.upload(Array.from(fileList))
      setDocuments(response)
      userNotificationsContext.openSnackbar({ open: true, message: text.uploadedMessage })
    } catch (err: any) {
      console.error(err)
      userNotificationsContext.openSnackbar({
        open: true,
        message: err?.response?.data?.message || 'The evidence could not be uploaded. Please try again.',
        backgroundColor: 'text.primary',
      })
    } finally {
      setIsUploading(false)
    }
  }

  const removeHandler = (document: IEmissionDocument) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: text.removeMessage(document.filename),
      buttonCopy: 'Remove document',
      cancelButtonCopy: 'Cancel',
    }).then(async (confirmed) => {
      if (!confirmed) return
      try {
        const response = await api.remove(document.id)
        setDocuments(response)
        userNotificationsContext.openSnackbar({ open: true, message: text.removedMessage })
      } catch (err: any) {
        console.error(err)
        userNotificationsContext.openSnackbar({
          open: true,
          message: err?.response?.data?.message || 'The document could not be removed. Please try again.',
          backgroundColor: 'text.primary',
        })
      }
    })
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200, py: 4 }}>
        <LoadingSpinner />
      </Box>
    )
  }

  if (errorMessage) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color='error'>{errorMessage}</Typography>
      </Box>
    )
  }

  return (
    <Stack spacing={2}>
      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
        {text.intro}
      </Typography>
      {documents.length === 0 && (
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          {text.empty}
        </Typography>
      )}
      {documents.length > 0 && (
        <Stack spacing={1}>
          {documents.map((document) => (
            <Box key={document.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
              <InsertDriveFileOutlined fontSize='small' sx={{ color: 'text.secondary' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant='body2' noWrap>{document.filename}</Typography>
                <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                  {[formatSize(document.size), document.createdAt ? moment(document.createdAt).format('DD/MM/YYYY') : ''].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              {document.details && (
                <Chip size='small' variant='outlined' label={document.details} sx={{ maxWidth: 260 }} />
              )}
              {previewable && isPreviewableDocument(document) && (
                <IconButton size='small' onClick={() => setPreviewDocument(document)} aria-label='View document'>
                  <VisibilityOutlined fontSize='small' />
                </IconButton>
              )}
              <IconButton size='small' onClick={() => uploadedResourceService.downloadFile(document.id, document.filename)} aria-label='Download document'>
                <DownloadOutlined fontSize='small' />
              </IconButton>
              {canEdit && (
                <IconButton size='small' onClick={() => removeHandler(document)}>
                  <Close fontSize='small' />
                </IconButton>
              )}
            </Box>
          ))}
        </Stack>
      )}
      {previewable && (
        <DocumentPreviewDialog
          open={previewDocument !== null}
          onClose={() => setPreviewDocument(null)}
          document={previewDocument}
        />
      )}
      {canEdit && (
        <Button
          component='label'
          variant='outlined'
          disabled={isUploading}
          startIcon={isUploading ? <CircularProgress size={16} /> : <UploadFileOutlined />}
          sx={{ alignSelf: 'flex-start' }}
        >
          {isUploading ? 'Uploading' : text.uploadLabel}
          <input
            type='file'
            hidden
            multiple
            accept={accept}
            onChange={(event) => {
              uploadHandler(event.target.files)
              event.target.value = ''
            }}
          />
        </Button>
      )}
    </Stack>
  )
}
